import { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PhoneInputField } from '../UISystem/Forms/PhoneInputField';
import { ButtonRounded } from '../UISystem/Buttons';
import ContactTypeDropdown from '../UISystem/Forms/ContactTypeDropdown';
import PillInput, { AtPrefix } from '../UISystem/Forms/PillInput';
import { COLORS, TYPOGRAPHY, SHADOWS, SURFACE_TOKENS } from '../../styles/UISystem';
import {
  ALL_CONTACT_TYPES,
  getAvailableContactTypes,
  getDefaultContactType,
} from '../../utils/contactTypeConfig';
import { sharedWithLabel } from '../../utils/contactUtils';
import { createAutofillDetector } from '../../utils/autofillDetection';

/**
 * AnonShareOverlay
 *
 * The single overlay for the Anon's contact share — serves both first-share and
 * later edits ("+" / card tap, pre-filled). Always user-initiated (the decision
 * sheet's "Share contact" button, or the post-share "+"), never auto-raised:
 * the deliberate act of sharing is the point, so it earns the standard scrim
 * treatment (§4.2) — darkened backdrop, tap-outside to dismiss, no X. Only the
 * final submit is automated: iOS autofill on the contact field auto-submits
 * (createAutofillDetector) so a filled card never needs the Share tap.
 *
 * TITLED "Shared with {FirstName}" (user design 2026-08-20), which is what
 * turns this from a form into a ledger. It is now the destination of tapping
 * your own contact card in the thread, and the question that tap asks is "what
 * does this person have of mine?" — the title answers it before the fields do,
 * and names the person so the answer is per-connection rather than global.
 * Rides the same phrase the Profile detail page uses over its sharing list,
 * including its 'them' degradation when the host's name hasn't landed.
 *
 * Name first, contact second, Done button below — one continuous form, two
 * fields. All fields share one shape/border (PillInput `bordered`, 1px) so none
 * reads heavier than another. Submit goes through handleUnifiedShareSubmit in
 * AnonConnectionPage (name chat bubble + contact data message in one action).
 *
 * "Done", not "Share" (same ruling): on the first share the button was the act,
 * but the overlay is reached from an existing card far more often than not, and
 * "Share" on a screen showing what you already shared reads as sending it a
 * second time. The write is identical either way — additive, idempotent on an
 * unchanged value (commitAnonContactRecord returns null on an identical
 * re-share) — so the label follows the common case.
 *
 * @param {boolean} show
 * @param {function} onSubmit - ({ name, contactType, contactValue })
 * @param {function} onDismiss - backdrop tap
 * @param {object} profileData - Profile's shared data (drives reciprocated default type)
 * @param {string} hostFirstName - the host's first name, for the title
 * @param {string} currentName - pre-fill name (edit re-entry)
 * @param {string} currentContactType - pre-fill contact type
 * @param {string} currentContactValue - pre-fill contact value
 */
const AnonShareOverlay = ({
  show,
  onSubmit,
  onDismiss,
  profileData,
  hostFirstName = '',
  currentName = '',
  currentContactType = '',
  currentContactValue = '',
}) => {
  const defaultType = useMemo(() => getDefaultContactType(profileData), [profileData]);

  const [name, setName] = useState(currentName);
  const [contactType, setContactType] = useState(currentContactType || defaultType);
  const [contactValue, setContactValue] = useState(currentContactValue);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [phoneFocused, setPhoneFocused] = useState(false);
  const [autoSubmitPending, setAutoSubmitPending] = useState(false);

  const nameInputRef = useRef(null);
  // Autofill auto-submit — contact field only (name autofill never submits).
  // Recreated on each open so a prior open's timestamps can't feed detection.
  const autofillDetectorRef = useRef(null);

  // Sync default type when Profile's data arrives (before the user picks a type)
  useEffect(() => {
    if (!currentContactType) setContactType(defaultType);
  }, [defaultType, currentContactType]);

  // Sync fields to props each time the sheet opens
  useEffect(() => {
    if (show) {
      setName(currentName);
      setContactType(currentContactType || defaultType);
      setContactValue(currentContactValue);
      setShowTypePicker(false);
      setPhoneFocused(false);
      setAutoSubmitPending(false);
      autofillDetectorRef.current = createAutofillDetector(Date.now());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Autofocus the name field (top) after the sheet animates in
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => nameInputRef.current?.focus(), 350);
    return () => clearTimeout(t);
  }, [show]);

  // Body scroll lock — standard overlay treatment
  useEffect(() => {
    if (!show) return;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [show]);

  // Clear the value when the user switches to a different type — don't carry a
  // phone number into an Instagram handle, etc. The prefilled type keeps its value.
  useEffect(() => {
    if (contactType !== currentContactType) {
      setContactValue('');
    }
  }, [contactType, currentContactType]);

  // Dropdown shows every available type EXCEPT the one currently selected
  // ("just the other ones").
  const dropdownTypes = useMemo(() => {
    const all = getAvailableContactTypes(profileData);
    return all.filter((t) => t.id !== contactType);
  }, [profileData, contactType]);

  const typeConfig = ALL_CONTACT_TYPES[contactType];
  const trimmedContact = typeof contactValue === 'string' ? contactValue.trim() : contactValue;
  const canSubmit = name.trim().length > 0 && !!trimmedContact;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({ name: name.trim(), contactType, contactValue: trimmedContact });
  };

  // Contact-field change: record the keystroke gap; a detected autofill burst
  // arms the pending flag (submit happens post-settle in the effect below).
  const handleContactChange = (value) => {
    setContactValue(value);
    const detector = autofillDetectorRef.current;
    if (detector) {
      detector.track();
      if (detector.wasAutofilled()) setAutoSubmitPending(true);
    }
  };

  // Auto-submit after autofill: wait for the value to settle (100ms with no
  // further contact changes — submitting inside the onChange tick truncated the
  // value historically), then submit through the normal handleSubmit path. If
  // the name is still empty at settle time, the flag drops without submitting.
  useEffect(() => {
    if (!autoSubmitPending) return;
    const t = setTimeout(() => {
      setAutoSubmitPending(false);
      autofillDetectorRef.current?.reset();
      if (canSubmit) handleSubmit();
    }, 100);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSubmitPending, contactValue]);

  const handleTypeSelect = (type) => {
    setContactType(type);
    setShowTypePicker(false);
  };

  const editLink = (
    <div
      onClick={() => setShowTypePicker(true)}
      style={{
        flexShrink: 0,
        cursor: 'pointer',
        padding: '8px',
        display: 'flex',
        alignItems: 'center',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span style={{ ...TYPOGRAPHY.BODY_LIGHT, fontSize: '15px', color: COLORS.MEDIUM_GRAY }}>
        Edit
      </span>
    </div>
  );

  const renderContactInput = () => {
    if (!typeConfig) return null;
    const { category, inputMode } = typeConfig;
    const isAtPrefix = category === 'at-prefix';
    // Placeholder = the type label ("Phone" / "Instagram" / "Email")
    const placeholder = typeConfig.label;

    // Phone-like: route through PillInput (inputSlot) so the container — border,
    // radius, padding, height — is byte-identical to the name + text fields.
    if (category === 'phone-like') {
      return (
        <PillInput
          inputSlot={
            // height 48px = PillInput's default input box (20px × 1.4 + 10px×2),
            // so the phone field renders the same 58px as the other fields.
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                height: '48px',
              }}
            >
              <PhoneInputField
                value={contactValue}
                onChange={handleContactChange}
                placeholder={placeholder}
                autoComplete="tel"
                embedded={true}
                hideDialCode={!phoneFocused}
                onFocus={() => setPhoneFocused(true)}
                onBlur={() => setPhoneFocused(false)}
              />
            </div>
          }
        >
          {editLink}
        </PillInput>
      );
    }

    return (
      <PillInput
        type={inputMode === 'email' ? 'email' : 'text'}
        inputMode={inputMode}
        autoCapitalize="none"
        // 'nope' parity with AnonShareBackFlow (review 2026-08-09): iOS ignores
        // autoComplete="off" and offers contact fills on handle fields — the
        // QuickType fix must live in BOTH copies or a D1 flip-back revives it
        autoComplete={inputMode === 'email' ? 'email' : 'nope'}
        value={contactValue}
        onChange={(e) => {
          let val = e.target.value;
          if (isAtPrefix) val = val.replace(/^@/, '').toLowerCase();
          handleContactChange(val);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && canSubmit) handleSubmit();
        }}
        placeholder={placeholder}
        prefix={isAtPrefix ? AtPrefix : null}
      >
        {editLink}
      </PillInput>
    );
  };

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Darkened backdrop — tap to dismiss (user-initiated overlay earns the scrim) */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onDismiss}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1000,
              backgroundColor: SURFACE_TOKENS.OVERLAY_BACKDROP,
            }}
          />

          {/* Bottom sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0, transition: { type: 'spring', damping: 30, stiffness: 300 } }}
            exit={{ y: '100%', transition: { duration: 0.3, ease: 'easeIn' } }}
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1001,
              backgroundColor: '#FFFFFF',
              borderRadius: '24px 24px 0 0',
              boxShadow: SHADOWS.TIER3,
              padding: '24px 20px calc(28px + env(safe-area-inset-bottom))',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
            }}
          >
            {/* One <form> for semantic autofill hints only — the combined one-tap
                AutoFill-Contact NEVER reliably fired on device (D2 record,
                tiered-exchange 2026-08-07); per-field autofill + the burst
                detector above are the real path. onSubmit preventDefault stops
                a page reload; the field onKeyDown + button onClick still drive submit,
                and the Share button is type="button" so a click doesn't double-fire. */}
            {/* Same weight/size as AnonThreadShareSheet's step titles — the two
                Anon sheets are the same object at different moments, so they
                cannot look like two different sheets. */}
            <div
              style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#000000',
                textAlign: 'center',
              }}
            >
              {sharedWithLabel(hostFirstName)}
            </div>

            <form
              onSubmit={(e) => e.preventDefault()}
              style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}
            >
              {/* Name */}
              <PillInput
                ref={nameInputRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canSubmit) handleSubmit();
                }}
                placeholder="Name"
                autoComplete="name"
                autoCapitalize="words"
              />

              {/* Contact + type picker */}
              <div style={{ position: 'relative' }}>
                {renderContactInput()}
                <ContactTypeDropdown
                  open={showTypePicker}
                  types={dropdownTypes}
                  onSelect={handleTypeSelect}
                  onClose={() => setShowTypePicker(false)}
                />
              </div>

              {/* Done — centered, gray until name + contact both present */}
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '6px' }}>
                <ButtonRounded
                  text="Done"
                  variant="primary"
                  size="large"
                  type="button"
                  disabled={!canSubmit}
                  onClick={handleSubmit}
                />
              </div>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AnonShareOverlay;
